// src/components/FolderContentArea/FolderContentArea.jsx
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { FlexGroup, FlexItem, Icon, SegmentControl, Table, Text, useTable } from '@zenqms/zui';
import { handleGenericError } from '../../handle-generic-error';
import { displayToast } from '../../app/app-slice';
import { sessionExpiredDetected } from '../../auth/auth-slice';
import {
  buildAggregatedColumns,
  buildAggregatedFilterColumns,
  buildDocumentColumns,
  buildDocumentFilterColumns,
  buildWorkflowColumns,
  buildWorkflowFilterColumns,
  buildTaskColumns,
  buildTaskFilterColumns,
  buildTrainingRecordColumns,
  buildTrainingRecordFilterColumns,
  buildFileColumns,
  buildFileFilterColumns,
  resolveModuleLabel,
  toZuiIconName,
  DOCUMENT_HIDDEN_COLUMNS,
  WORKFLOW_HIDDEN_COLUMNS,
  TASK_HIDDEN_COLUMNS,
  TRAINING_RECORD_HIDDEN_COLUMNS,
} from './folderContentAreaColumns';
import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import './FolderContentArea.scss';

const ALL_TYPES_VALUE = '__all_types__';
const DOCUMENTS_VALUE = 'document';
const TASKS_VALUE = 'task';
// Matches the `training_record` audit_module the documents service emits for the
// training-records facet, so the segment value doubles as the facet key.
const TRAINING_RECORDS_VALUE = 'training_record';
// Matches the `file` audit_module the documents service emits for the files facet.
const FILES_VALUE = 'file';

// The documents service clamps page_size at 100 (@zenqms/defaults getPaginationQuery) but the
// controller echoes back whatever was REQUESTED, so an over-sized request silently under-fetches:
// the offset is computed from the clamped size while the client pages as if it got the larger one.
// pageSizeOptions below stays well under this, but clamp explicitly so raising them can't
// reintroduce the bug.
const TRAINING_RECORD_MAX_PAGE_SIZE = 100;

export const WORKFLOW_REQUEST_COLUMNS = [
  'key_id',
  'name',
  'current_stage',
  'stage_assignees',
  'stage_due_date',
  'project_assignees',
  'status',
  'item_id',
  'audit_module',
];

// ─── API ──────────────────────────────────────────────────────────────────────

async function searchItems(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/items`, body);
  return r.data;
}

async function searchDocuments(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/documents`, body);
  return r.data;
}

async function searchWorkflows(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/workflows`, body);
  return r.data;
}

async function searchTasks(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/tasks`, body);
  return r.data;
}

async function searchTrainingRecords(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/training-records`, body);
  return r.data;
}

async function searchFiles(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/files`, body);
  return r.data;
}

// Returns { url, fileExtension, watermarkOptions }. For a video revision `url` is the raw presigned
// object (not a PDF) and `watermarkOptions` is what to draw over it; for a PDF it is null, because
// that watermark is already burned in. All of them are null when the document has no revision.
async function fetchDocumentPdfUrl(url, shareId, folderId, documentVersionId) {
  const r = await axios.get(
    `${url}/share/folders/${shareId}/folders/${folderId}/documents/${documentVersionId}/pdf-url`
  );
  return r.data;
}

async function fetchWorkflowPdfUrl(url, shareId, folderId, workflowId) {
  const r = await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}/workflows/${workflowId}/pdf-url`);
  return r.data;
}

async function fetchTaskPdfUrl(url, shareId, folderId, taskId) {
  const r = await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}/tasks/${taskId}/pdf-url`);
  return r.data;
}

// Returns { url, fileExtension }: the file rendered as a PDF, or the raw stored object for a
// playable video. `fileExtension` is the file's stored extension and is what tells the two apart.
async function fetchFilePdfUrl(url, shareId, folderId, fileId) {
  const r = await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}/files/${fileId}/pdf-url`);
  return r.data;
}

async function checkFolderExists(url, shareId, folderId) {
  try {
    await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}`);
    return true;
  } catch (err) {
    const status = err?.response?.status;
    return !(status === 404 || status === 410 || status === 403);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extracts the lowercase file extension from a pre-signed S3 URL.
 * Pre-signed URLs carry query parameters after a `?`; we only inspect the
 * path portion so the signature tokens don't pollute the extension match.
 *
 * Returns e.g. 'pdf', 'mp4', 'docx', or null if no extension is found.
 */
function extractFileExtension(url) {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() ?? '';
    const dotIndex = lastSegment.lastIndexOf('.');
    if (dotIndex === -1) return null;
    return lastSegment.slice(dotIndex + 1).toLowerCase();
  } catch {
    return null;
  }
}

// ─── EmptyFolderState ─────────────────────────────────────────────────────────

function EmptyFolderState({ folderName }) {
  const { formatMessage } = useIntl();
  return (
    <div className="folder-content-area__empty-state">
      <div className="folder-content-area__empty-icon-wrapper">
        <Icon name="folder" size="lg" noHover className="folder-content-area__empty-icon" />
      </div>
      <p className="folder-content-area__empty-title">
        {formatMessage({ id: 'folderContentArea.emptyState.title', defaultMessage: 'This folder is empty' })}
      </p>
      <p className="folder-content-area__empty-subtitle">
        {formatMessage(
          {
            id: 'folderContentArea.emptyState.subtitle',
            defaultMessage: '"{folderName}" does not contain any items yet.',
          },
          { folderName }
        )}
      </p>
    </div>
  );
}

// ─── FolderDataTable ──────────────────────────────────────────────────────────

/**
 * Single-view data table.
 *
 * For the aggregated ALL_TYPES_VALUE view: calls /items, extracts
 * facets.types from the response and passes them up via onAggregatedFacets.
 *
 * For DOCUMENTS_VALUE: calls /documents.
 *
 * For any other value (a workflow audit_module string): calls /items with
 * an auditModuleFilter pre-applied in the request body.
 */
function FolderDataTable({
  folderShareId,
  folderId,
  view,
  auditModuleFilter,
  onEmptyChange,
  onOpenDocument,
  onAggregatedFacets,
  aggregatedFacets = [],
  allowDownload = false,
  enabled = true,
  refreshKey = 0,
  onFolderGone,
}) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const zensignApiUrl = window.config.zensignApiUrl;

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Derive a single viewType instead of four boolean flags.
  // NOTE: the catch-all is 'workflow' — a new tab without an explicit branch here silently
  // POSTs to /workflows and renders workflow columns.
  const viewType =
    view === ALL_TYPES_VALUE
      ? 'aggregated'
      : view === DOCUMENTS_VALUE
      ? 'document'
      : view === TASKS_VALUE
      ? 'task'
      : view === TRAINING_RECORDS_VALUE
      ? 'training_record'
      : view === FILES_VALUE
      ? 'file'
      : 'workflow';

  const onAggregatedFacetsRef = useRef(onAggregatedFacets);
  onAggregatedFacetsRef.current = onAggregatedFacets;

  const columns = useMemo(() => {
    const downloadContext = allowDownload ? { folderShareId, folderId } : null;
    if (viewType === 'document') return buildDocumentColumns(formatMessage, onOpenDocument, downloadContext);
    if (viewType === 'task') return buildTaskColumns(formatMessage, onOpenDocument, downloadContext);
    // No downloadContext: the Training Records tab has no download (TRAIN-2410).
    if (viewType === 'training_record') return buildTrainingRecordColumns(formatMessage, onOpenDocument);
    if (viewType === 'file') return buildFileColumns(formatMessage, onOpenDocument, downloadContext);
    if (viewType === 'workflow') return buildWorkflowColumns(formatMessage, onOpenDocument, downloadContext);
    return buildAggregatedColumns(formatMessage, aggregatedFacets, onOpenDocument, downloadContext);
  }, [viewType, onOpenDocument, formatMessage, aggregatedFacets, allowDownload, folderShareId, folderId]);

  const filterColumns = useMemo(() => {
    if (viewType === 'document') return buildDocumentFilterColumns(formatMessage);
    if (viewType === 'task') return buildTaskFilterColumns(formatMessage);
    if (viewType === 'training_record') return buildTrainingRecordFilterColumns(formatMessage);
    if (viewType === 'file') return buildFileFilterColumns(formatMessage);
    if (viewType === 'workflow') return buildWorkflowFilterColumns(formatMessage);
    return buildAggregatedFilterColumns(formatMessage, aggregatedFacets);
  }, [viewType, formatMessage, aggregatedFacets]);

  const hiddenColumns =
    viewType === 'document'
      ? DOCUMENT_HIDDEN_COLUMNS
      : viewType === 'workflow'
      ? WORKFLOW_HIDDEN_COLUMNS
      : viewType === 'task'
      ? TASK_HIDDEN_COLUMNS
      : viewType === 'training_record'
      ? TRAINING_RECORD_HIDDEN_COLUMNS
      : {};

  const table = useTable({
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 25],
    filters: { columns: filterColumns },
    ...(Object.keys(hiddenColumns).length > 0 && { initialColumnVisibility: hiddenColumns }),
  });

  const { stateForApi, filters, setTotalRecords: setTableTotal } = table;

  const onEmptyChangeRef = useRef(onEmptyChange);
  onEmptyChangeRef.current = onEmptyChange;

  const onFolderGoneRef = useRef(onFolderGone);
  onFolderGoneRef.current = onFolderGone;

  const fetchData = useCallback(async () => {
    if (!folderShareId || !folderId || !enabled) return;
    setIsLoading(true);
    try {
      // Build filter list: for workflow tabs prepend the audit_module filter (mirrors FoldersTable.tsx)
      const baseFilters = filters.filtersForApi ?? [];
      const requestFilters =
        auditModuleFilter && viewType === 'workflow'
          ? [{ columnName: 'audit_module', value: auditModuleFilter, operation: 'equal' }, ...baseFilters]
          : baseFilters;

      const body = {
        query: {
          current_page: stateForApi.pageIndex,
          page_size:
            viewType === 'training_record'
              ? Math.min(stateForApi.pageSize, TRAINING_RECORD_MAX_PAGE_SIZE)
              : stateForApi.pageSize,
          ...(stateForApi.sorting?.[0] && {
            sort_by: stateForApi.sorting[0].id,
            order_by: stateForApi.sorting[0].desc ? 'desc' : 'asc',
          }),
        },
        filters: requestFilters,
        ...(viewType === 'workflow' && { columns: WORKFLOW_REQUEST_COLUMNS }),
      };

      const result =
        viewType === 'document'
          ? await searchDocuments(zensignApiUrl, folderShareId, folderId, body)
          : viewType === 'task'
          ? await searchTasks(zensignApiUrl, folderShareId, folderId, body)
          : viewType === 'training_record'
          ? await searchTrainingRecords(zensignApiUrl, folderShareId, folderId, body)
          : viewType === 'file'
          ? await searchFiles(zensignApiUrl, folderShareId, folderId, body)
          : viewType === 'workflow'
          ? await searchWorkflows(zensignApiUrl, folderShareId, folderId, body)
          : await searchItems(zensignApiUrl, folderShareId, folderId, body);

      const rows = result.data ?? [];
      const total = result.pagination?.total_records ?? rows.length;
      setData(rows);
      setTotalRecords(total);
      setTableTotal(total);

      if (viewType === 'aggregated') {
        const apiFacets = result.facets?.types ?? [];
        // If the API returned document rows but no document facet (backend limitation:
        // folders_workflow_types_view doesn't include a document row), synthesise one.
        const hasDocumentFacet = apiFacets.some((f) => f.audit_module === 'document');
        const hasDocumentRows = (result.data ?? []).some((r) => !r.audit_module || r.audit_module === 'document');
        const baseFacets =
          !hasDocumentFacet && hasDocumentRows
            ? [{ audit_module: 'document', plural_name: 'Documents', icon: 'file', count: 0 }, ...apiFacets]
            : apiFacets;

        // Back-fill missing facet icons from per-row item_type_icon data.
        // The API sometimes returns facets with an empty icon field; the
        // rows themselves always carry item_type_icon from the DB view.
        const rowIconByModule = {};
        (result.data ?? []).forEach((r) => {
          const mod = r.audit_module?.toLowerCase() || 'document';
          if (r.item_type_icon && !rowIconByModule[mod]) rowIconByModule[mod] = r.item_type_icon;
        });
        const facets = baseFacets.map((f) => (f.icon ? f : { ...f, icon: rowIconByModule[f.audit_module] ?? '' }));

        if (facets.length > 0) onAggregatedFacetsRef.current(facets);
        // The aggregated /items search lists training-record rows as of TRAIN-2442; a documents
        // service without that change reports only has_training_records, so a folder holding
        // nothing but training records comes back with total 0. Without this guard it would render
        // the whole-folder empty state, which hides the segment control and makes the Training
        // Records tab unreachable. Mirrors `!hasTrainingRecords` in the internal FoldersTable.tsx isFolderEmpty.
        const hasTrainingRecords = result.facets?.has_training_records === true;
        onEmptyChangeRef.current(total === 0 && !filters.hasActiveFilters && !hasTrainingRecords);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        dispatch(sessionExpiredDetected());
        return;
      } else if (status === 403) {
        dispatch(displayToast(formatMessage({ id: 'errors.itemNotFound' })));
        onFolderGoneRef.current?.();
      } else if (status === 404 || status === 410) {
        dispatch(displayToast(formatMessage({ id: 'errors.itemNotFound' })));
        onFolderGoneRef.current?.();
      } else {
        handleGenericError(dispatch, err, navigate);
        dispatch(displayToast(formatMessage({ id: 'errors.somethingWentWrong' })));
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    folderShareId,
    folderId,
    viewType,
    auditModuleFilter,
    enabled,
    refreshKey,
    stateForApi,
    filters.filtersForApi,
    filters.hasActiveFilters,
    zensignApiUrl,
    dispatch,
    navigate,
    setTableTotal,
    formatMessage,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Row id resolution. training_record_id must win for the Training Records view: those rows
  // ALSO carry item_id (the trained item), which is not unique — one item yields one row per
  // trained user — so the generic chain below would collapse them onto the same row id.
  // Stringified because training_record_id is numeric while every other bucket id is a UUID string.
  // The id is resolved BEFORE stringifying: String(undefined) would yield the literal "undefined",
  // which survives the .filter(Boolean) below and collapses every such row onto one row id.
  const getRowId = useCallback(
    (row) => {
      if (viewType !== 'training_record') {
        // All types rows from training records (TRAIN-2442) carry none of the uuid ids; without
        // training_record_id they would fall through to key_id/name. Those hold the trained item's
        // key ID and name, which repeat once per trained user. Mirrors the internal FoldersTable.tsx id chain.
        const trainingRowId =
          row.training_record_id === undefined || row.training_record_id === null
            ? undefined
            : String(row.training_record_id);
        // File rows (Files tab and All types) carry only file_id among the uuid ids.
        return (
          row.document_version_id ??
          row.item_id ??
          row.task_id ??
          row.file_id ??
          trainingRowId ??
          row.id ??
          row.key_id ??
          row.name
        );
      }
      const trainingRowId = row.training_record_id ?? row.id;
      return trainingRowId === undefined || trainingRowId === null ? undefined : String(trainingRowId);
    },
    [viewType]
  );

  useEffect(() => {
    table.setVisibleRowIds(data.map((row) => getRowId(row)).filter(Boolean));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // The shared fallback copy ("No items match the current filters") is only accurate when a filter
  // is actually applied; with a filter active it still wins for every view. Two views can
  // legitimately come back empty with no filter at all and need unfiltered copy instead:
  //   • training_record — nothing has been linked to the folder yet. Mirrors the internal app's
  //     TrainingRecordsEmptyState title and graduation-cap icon, but WITHOUT its subtitle ("Search
  //     and select the training records you want to pull into this folder") and WITHOUT its "Link
  //     Training Records" button — an external auditor can neither search nor link (TRAIN-2410).
  //   • aggregated — a documents service without TRAIN-2442 does not list training-record rows,
  //     so a folder holding only training records reports total 0 here while staying non-empty
  //     overall (see the has_training_records guard in fetchData). That state is new as of
  //     TRAIN-2410: such a folder previously rendered the whole-folder "This folder is empty" state.
  const unfiltered = !filters.hasActiveFilters;
  const emptyStateContent =
    unfiltered && viewType === 'training_record'
      ? {
          icon: 'graduation-cap',
          message: formatMessage({
            id: 'folderContentArea.trainingRecords.emptyState.title',
            defaultMessage: 'No training records linked yet',
          }),
        }
      : unfiltered && viewType === 'aggregated'
      ? {
          icon: 'folder-open',
          message: formatMessage({ id: 'folderContentArea.noItems', defaultMessage: 'No items to display.' }),
        }
      : {
          icon: 'folder-open',
          message: formatMessage({
            id: 'folderContentArea.empty',
            defaultMessage: 'No items match the current filters.',
          }),
        };

  const emptyState = (
    <FlexGroup direction="column" alignItems="center" gap="md" style={{ padding: '40px 0' }}>
      <Icon name={emptyStateContent.icon} size="xl" noHover color="var(--zui-color-gray-400)" />
      <Text size="md">{emptyStateContent.message}</Text>
    </FlexGroup>
  );

  return (
    <Table
      {...table.tableProps}
      filterColumns={filterColumns}
      columns={columns}
      data={data}
      totalRecords={totalRecords}
      isLoading={isLoading}
      getRowId={getRowId}
      enableColumnDnD={false}
    >
      <Table.Toolbar>
        <Table.Toolbar.Right>
          <Table.Toolbar.ManageColumns />
        </Table.Toolbar.Right>
      </Table.Toolbar>
      <Table.Content emptyState={emptyState} />
      <Table.Footer />
    </Table>
  );
}

// ─── FolderContentArea ────────────────────────────────────────────────────────

export function FolderContentArea({ folderShareId, folderId, folderName, allowDownload, onFolderGone }) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const zensignApiUrl = window.config.zensignApiUrl;

  // selectedSegment is either ALL_TYPES_VALUE, DOCUMENTS_VALUE, or an audit_module string
  const [selectedSegment, setSelectedSegment] = useState(ALL_TYPES_VALUE);
  // facets lifted from the aggregated fetch — drives the segment items dynamically
  const [facets, setFacets] = useState([]);
  const [isFolderEmpty, setIsFolderEmpty] = useState(false);
  const [resolvedFolderId, setResolvedFolderId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Document preview panel state ──────────────────────────────────────────
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFileExtension, setPreviewFileExtension] = useState(null);
  // The watermark to draw over a video (DM-8751). It comes from the pdf-url response of the preview
  // that is open, and is cleared with it so the next document never starts out with the previous
  // one's watermark.
  const [previewWatermarkOptions, setPreviewWatermarkOptions] = useState(null);
  // True when the opened document has no revision attached (e.g. a draft): the documents service
  // answers 200 with `url: null` for that, which is a normal state rather than a missing item.
  const [previewNoRevision, setPreviewNoRevision] = useState(false);

  // Reset all per-folder state when folderId changes
  useEffect(() => {
    setSelectedSegment(ALL_TYPES_VALUE);
    setFacets([]);
    setIsFolderEmpty(false);
    setResolvedFolderId(null);
    setPreviewOpen(false);
    setPreviewFileExtension(null);
    setPreviewWatermarkOptions(null);
    setPreviewNoRevision(false);
  }, [folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmptyChange = useCallback(
    (isEmpty) => {
      setIsFolderEmpty(isEmpty);
      setResolvedFolderId(folderId);
    },
    [folderId]
  );

  const handleOpenDocument = useCallback(
    async (id, documentName, itemType) => {
      // Explicit per-type routing. A training record's id is its document version, so it previews
      // through the documents endpoint, which accepts the document of a record linked to the folder.
      const fetcher =
        itemType === 'task'
          ? fetchTaskPdfUrl
          : itemType === 'workflow'
          ? fetchWorkflowPdfUrl
          : itemType === 'file'
          ? fetchFilePdfUrl
          : fetchDocumentPdfUrl;

      setPreviewName(documentName ?? '');
      setPreviewUrl(null);
      setPreviewFileExtension(null);
      setPreviewWatermarkOptions(null);
      setPreviewNoRevision(false);
      setPreviewLoading(true);
      setPreviewOpen(true);
      try {
        // Fetched on every open and never cached: for a video the response carries the watermark,
        // whose "printed on" time is the time of this request.
        const { url, fileExtension, watermarkOptions } = await fetcher(zensignApiUrl, folderShareId, folderId, id);
        setPreviewUrl(url);
        // Only documents (a training record previews one) can legitimately come back without a url;
        // tasks, workflows and files always resolve to one, so a missing url there stays a blank
        // panel rather than being mislabelled as a missing revision.
        setPreviewNoRevision((itemType === 'document' || itemType === 'training_record') && !url);
        // The renderer follows the API's extension: a playable video resolves to the raw stored
        // object, whose url (an opaque S3 key) carries no extension to read, and PDFtron has to
        // be told it is a video to open it in the video player rather than the PDF loader. Anything
        // that is not a video loads as a PDF, so a 'docx' here still renders its generated PDF
        // correctly. Tasks, workflows and a documents service predating the field send none, and
        // their url does end in the extension, so it stays the fallback.
        setPreviewFileExtension(fileExtension || extractFileExtension(url));
        // The response's own object, never rebuilt: its identity stays steady across re-renders.
        // Only a document's video gets one — a PDF is already watermarked and files deliberately
        // are not — so null is the common answer and means there is nothing to draw.
        setPreviewWatermarkOptions(watermarkOptions ?? null);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          setPreviewOpen(false);
          dispatch(sessionExpiredDetected());
          return;
        } else if (status === 403) {
          dispatch(
            displayToast(
              formatMessage({
                id: 'errors.itemNoLongerAvailable',
                defaultMessage: 'This item is no longer available.',
              })
            )
          );
          onFolderGone?.();
        } else if (status === 404 || status === 410) {
          const folderStillExists = await checkFolderExists(zensignApiUrl, folderShareId, folderId);
          dispatch(
            displayToast(
              formatMessage({
                id: 'errors.itemNoLongerAvailable',
                defaultMessage: 'This item is no longer available.',
              })
            )
          );
          if (folderStillExists) {
            setRefreshKey((key) => key + 1);
          } else {
            onFolderGone?.();
          }
        } else {
          handleGenericError(dispatch, err, navigate);
          dispatch(
            displayToast(
              formatMessage({
                id: 'errors.somethingWentWrong',
                defaultMessage: 'Something went wrong. Please try again.',
              })
            )
          );
        }
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    },
    [zensignApiUrl, folderShareId, folderId, onFolderGone, dispatch, navigate, formatMessage]
  );

  // Sort facets: Documents first, then alphabetical by plural_name.
  // Mirrors the UI workspace sort in FoldersContentArea.tsx.
  const sortedFacets = useMemo(() => {
    return [...facets].sort((a, b) => {
      if (a.audit_module === DOCUMENTS_VALUE) return -1;
      if (b.audit_module === DOCUMENTS_VALUE) return 1;
      return a.plural_name.localeCompare(b.plural_name, undefined, { sensitivity: 'base' });
    });
  }, [facets]);

  // Build segment items dynamically: "All types" + one per facet from the API.
  // - Documents facet → hits /documents endpoint (same as before)
  // - Any other facet → hits /items endpoint with audit_module filter pre-applied
  const segmentItems = useMemo(() => {
    const items = [
      {
        value: ALL_TYPES_VALUE,
        label: formatMessage({ id: 'folderContentArea.segment.allTypes', defaultMessage: 'All types' }),
        icon: 'grid',
      },
    ];
    sortedFacets.forEach((facet) => {
      const isDoc = facet.audit_module === DOCUMENTS_VALUE;
      const icon = isDoc ? 'file' : toZuiIconName(facet.icon) ?? 'diagram-project';
      items.push({
        value: facet.audit_module,
        label: resolveModuleLabel(facet.audit_module, sortedFacets),
        icon,
      });
    });
    return items;
  }, [sortedFacets, formatMessage]);

  if (!folderShareId || !folderId) {
    return (
      <FlexGroup
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="folder-content-area folder-content-area--no-selection"
      >
        <Icon name="folder" size="xl" noHover color="var(--zui-color-gray-300)" />
        <Text size="md">
          {formatMessage({
            id: 'folderContentArea.noFolderSelected',
            defaultMessage: 'Select a folder to view its contents.',
          })}
        </Text>
      </FlexGroup>
    );
  }

  const isFolderLoading = resolvedFolderId !== folderId;
  const showEmptyState = !isFolderLoading && isFolderEmpty;

  // Derive a single segment type to drive rendering.
  const segmentType =
    selectedSegment === ALL_TYPES_VALUE
      ? 'aggregated'
      : selectedSegment === DOCUMENTS_VALUE
      ? 'document'
      : selectedSegment === TASKS_VALUE
      ? 'task'
      : selectedSegment === TRAINING_RECORDS_VALUE
      ? 'training_record'
      : selectedSegment === FILES_VALUE
      ? 'file'
      : 'workflow';
  const workflowAuditModule = segmentType === 'workflow' ? selectedSegment : null;

  return (
    <FlexGroup direction="column" className="folder-content-area folder-content-area--with-preview">
      <FlexItem grow={1} className="folder-content-area__main" style={{ minWidth: 0 }}>
        <FlexGroup direction="column" style={{ height: '100%' }}>
          {!showEmptyState && (
            <FlexItem className="folder-content-area__switcher">
              <SegmentControl
                aria-label={formatMessage({
                  id: 'folderContentArea.segment.ariaLabel',
                  defaultMessage: 'View switcher',
                })}
                value={selectedSegment}
                onValueChange={setSelectedSegment}
                items={segmentItems}
                size="md"
                maxWidth="100%"
              />
            </FlexItem>
          )}

          <FlexItem grow={1} className="folder-content-area__table-container">
            {showEmptyState && <EmptyFolderState folderName={folderName ?? ''} />}

            <div style={{ display: showEmptyState ? 'none' : undefined }}>
              {/* Aggregated (All types) — always mounted; feeds facets to segmentItems */}
              <div className={segmentType !== 'aggregated' ? 'folder-content-area__table--hidden' : undefined}>
                <FolderDataTable
                  key={`all-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={ALL_TYPES_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={handleEmptyChange}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={setFacets}
                  aggregatedFacets={sortedFacets}
                  allowDownload={allowDownload}
                  onFolderGone={onFolderGone}
                />
              </div>

              {/* Documents tab */}
              <div className={segmentType !== 'document' ? 'folder-content-area__table--hidden' : undefined}>
                <FolderDataTable
                  key={`docs-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={DOCUMENTS_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  enabled={segmentType === 'document'}
                  onFolderGone={onFolderGone}
                />
              </div>

              {/* Workflow type tab — one shared table, re-keyed on the selected audit_module
                  so each type gets its own pagination/filter state */}
              {segmentType === 'workflow' && (
                <FolderDataTable
                  key={`wf-${folderShareId}-${folderId}-${workflowAuditModule}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={workflowAuditModule}
                  auditModuleFilter={workflowAuditModule}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  onFolderGone={onFolderGone}
                />
              )}

              {/* Tasks tab */}
              {segmentType === 'task' && (
                <FolderDataTable
                  key={`tasks-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={TASKS_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  enabled={segmentType === 'task'}
                  onFolderGone={onFolderGone}
                />
              )}

              {/* Files tab — preview renders the file as a PDF; download (when the share allows it)
                  saves the original file, not a PDF */}
              {segmentType === 'file' && (
                <FolderDataTable
                  key={`files-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={FILES_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  enabled={segmentType === 'file'}
                  onFolderGone={onFolderGone}
                />
              )}

              {/* Training Records tab — allowDownload is deliberately withheld, so no download control
                  can reach this view even if a column here ever started consuming it (TRAIN-2410) */}
              {segmentType === 'training_record' && (
                <FolderDataTable
                  key={`training-records-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={TRAINING_RECORDS_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  enabled={segmentType === 'training_record'}
                  onFolderGone={onFolderGone}
                />
              )}
            </div>
          </FlexItem>
        </FlexGroup>
      </FlexItem>

      <DocumentPreviewPanel
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFileExtension(null);
          setPreviewWatermarkOptions(null);
          setPreviewNoRevision(false);
        }}
        documentName={previewName}
        url={previewUrl}
        isLoading={previewLoading}
        fileExtension={previewFileExtension}
        watermarkOptions={previewWatermarkOptions}
        noRevision={previewNoRevision}
      />
    </FlexGroup>
  );
}
